import {
  apiClient,
  ApiClientError,
  isRetryableFailure,
} from "@/lib/api/client";
import { invalidateSalesViews } from "@/lib/query";
import type { QueryClient } from "@tanstack/react-query";
import type { Order } from "@/types";
import { listQueue, markStatus, type QueuedOrder } from "./queue";

const SYNC_INTERVAL_MS = 30_000;
const RETRY_BACKOFF_MS = 5_000;

const MAX_RETRYABLE_ATTEMPTS = 20;

let timer: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<void> | null = null;
let queryClient: QueryClient | null = null;

export function startOfflineSync(qc: QueryClient) {
  queryClient = qc;
  if (timer !== null) return;
  void syncNow();
  timer = setInterval(() => {
    void syncNow();
  }, SYNC_INTERVAL_MS);

  window.addEventListener("online", onOnline);
}

export function stopOfflineSync() {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  window.removeEventListener("online", onOnline);
}

function onOnline() {
  void syncNow();
}

export async function syncNow(): Promise<void> {
  if (inflight) return inflight;
  inflight = drain().finally(() => {
    inflight = null;
  });

  return inflight;
}

async function drain(): Promise<void> {
  if (!navigator.onLine) return;

  const queue = await listQueue();
  if (queue.length === 0) return;

  for (const entry of queue) {
    if (entry.status === "synced" || entry.status === "syncing") continue;
    if (entry.status === "failed") continue; // requires manual retry/discard

    await syncOne(entry);
  }
}

async function syncOne(entry: QueuedOrder): Promise<void> {
  const now = Date.now();
  if (entry.lastAttemptAt && now - entry.lastAttemptAt < RETRY_BACKOFF_MS) {
    return;
  }

  await markStatus(entry.id, "syncing", {
    attemptCount: entry.attemptCount + 1,
    lastAttemptAt: now,
  });

  try {
    const order = await apiClient.post<Order>("/orders", entry.payload, {
      idempotencyKey: entry.idempotencyKey,
    });
    await markStatus(entry.id, "synced", { serverOrderId: order.id });
    if (queryClient) {
      invalidateSalesViews(queryClient);
    }
  } catch (err) {
    if (
      err instanceof ApiClientError &&
      err.details?.code === "IDEMPOTENCY_IN_FLIGHT"
    ) {
      await markStatus(entry.id, "pending", { error: err.message });

      return;
    }

    if (isRetryableFailure(err) && entry.attemptCount < MAX_RETRYABLE_ATTEMPTS) {
      await markStatus(entry.id, "pending", {
        error: err instanceof Error ? err.message : "Retryable failure",
      });

      return;
    }

    await markStatus(entry.id, "failed", {
      error: err instanceof Error ? err.message : "Unknown error",
    });
  }
}
